import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BetLimitCandidate, BetLimitProviderOption } from '@core/models/bet-limit-group.model';
import { Pagination } from '@core/models/pagination.model';
import { GroupHttpService } from '@core/services/group-http.service';
import { Subscription } from 'rxjs';
import Swal from 'sweetalert2';
import { BetLimitGroupDataService } from '../../services/bet-limit-group-data.service';

/** Rows per bulk check — the api accepts up to 5,000 usernames a request. */
const MAX_BULK = 5000;

/**
 * AIO-81599 — pick players for a bet limit group: search by username, or a bulk list (CSV file
 * or pasted). Returns the picked candidates; the Players step stages them until Save.
 *
 * Search needs 3+ characters of a username: members are counted in millions, so nothing is
 * listed unfiltered. A CSV is read in the browser (first column), so no upload endpoint exists.
 */
@Component({
  selector: 'kt-bet-limit-add-players',
  templateUrl: './add-players.component.html',
  styleUrls: ['./add-players.component.scss'],
})
export class AddPlayersDialogComponent implements OnInit, OnDestroy {

  readonly minSearch = 3;

  tab = 0;
  currencies = JSON.parse(sessionStorage.getItem('currencies')) ?? [];
  memberGroups: { id: number; name: string }[] = [];

  // kt-dropdown-wo-lazyload writes into a form control; filters.member_group_id is what the search reads.
  groupForm = new FormGroup({ member_group_id: new FormControl(null) });
  groupSelected: { id: number; name: string }[] = [];
  groupSettings = {
    singleSelection: true,
    text: 'All',
    enableFilterSelectAll: false,
    enableSearchFilter: true,
    classes: 'dropdown',
    maxHeight: 250,
    primaryKey: 'id',
    labelKey: 'name',
    noDataLabel: '',
    showCheckbox: false,
    autoPosition: false,
  };

  // Search
  filters = { username: '', currency_id: 'all', member_group_id: 'all' };
  candidates: BetLimitCandidate[] = [];
  pagination: Pagination;
  page = 1;
  searching = false;
  searched = false;

  // Bulk list
  bulkText = '';
  fileName = '';
  checking = false;
  bulkResult: { found: BetLimitCandidate[]; not_found: string[] } = null;

  /** Picked from either tab, kept across pages and searches. */
  picked = new Map<number, BetLimitCandidate>();

  private subscriptions = new Subscription();

  constructor(
    public dialogRef: MatDialogRef<AddPlayersDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { provider: BetLimitProviderOption; groupId: number | null; groupName: string },
    private betLimitGroupDataService: BetLimitGroupDataService,
    private groupHttpService: GroupHttpService,
  ) { }

  ngOnInit() {
    this.subscriptions.add(this.groupHttpService.getGroups().subscribe(groups => this.memberGroups = groups as any));
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  get canSearch(): boolean {
    return (this.filters.username || '').trim().length >= this.minSearch && !this.searching;
  }

  /** Already in this group: nothing to add. */
  isMember(c: BetLimitCandidate): boolean {
    return !!this.data.groupId && +c.current_group_id === +this.data.groupId;
  }

  /** In another group of the same provider: adding it moves the player here. */
  willMove(c: BetLimitCandidate): boolean {
    return !!c.current_group_id && !this.isMember(c);
  }

  onSearch(page = 1) {
    if (!this.canSearch) {
      return;
    }
    this.page = page;
    this.searching = true;
    const q = [
      `game_provider_id=${this.data.provider.game_provider_id}`,
      `username=${encodeURIComponent(this.filters.username.trim())}`,
      `page=${page}`,
    ];
    if (this.filters.currency_id !== 'all') {
      q.push(`currency_id=${this.filters.currency_id}`);
    }
    if (this.filters.member_group_id !== 'all') {
      q.push(`member_group_id=${this.filters.member_group_id}`);
    }

    this.subscriptions.add(this.betLimitGroupDataService.searchCandidates(`?${q.join('&')}`).subscribe(
      data => {
        this.candidates = data.rows;
        this.pagination = data.paginations;
        this.searching = false;
        this.searched = true;
      },
      () => this.searching = false
    ));
  }

  /** No selection (cleared with the x) means all member groups. */
  onMemberGroupChanged(items: { id: number }[]) {
    this.filters.member_group_id = items?.length ? String(items[0].id) : 'all';
  }

  onClear() {
    this.filters = { username: '', currency_id: 'all', member_group_id: 'all' };
    this.groupSelected = [];
    this.groupForm.controls.member_group_id.setValue(null, { emitEvent: false });
    this.candidates = [];
    this.pagination = undefined;
    this.searched = false;
  }

  onPick(c: BetLimitCandidate, checked: boolean) {
    checked ? this.picked.set(c.id, c) : this.picked.delete(c.id);
  }

  onPickPage() {
    this.candidates.filter(c => !this.isMember(c)).forEach(c => this.picked.set(c.id, c));
  }

  onUnpickPage() {
    this.candidates.forEach(c => this.picked.delete(c.id));
  }

  onUnpickAll() {
    this.picked.clear();
  }

  /** Any player on this results page is selected. */
  get pagePicked(): boolean {
    return this.candidates.some(c => this.picked.has(c.id));
  }

  /** Newest first, so a fresh pick shows at the start of the tags. */
  get pickedList(): BetLimitCandidate[] {
    return Array.from(this.picked.values()).reverse();
  }

  trackById(_: number, c: BetLimitCandidate) {
    return c.id;
  }

  onFile(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) {
      return;
    }
    // Read in the browser as text, so only CSV works: an .xlsx is a zip and reads as garbage.
    if (!/\.csv$/i.test(file.name)) {
      input.value = '';
      Swal.fire('System Message', `${file.name} is not a CSV file. In Excel, use File > Save As > CSV, then choose that file.`, 'error');
      return;
    }
    this.fileName = file.name;
    const reader = new FileReader();
    reader.onload = () => {
      // First column of each line; a header row is dropped by the server as "not found"
      // unless it is literally "username".
      const names = String(reader.result)
        .split(/\r?\n/)
        .map(line => line.split(',')[0].replace(/^"|"$/g, '').trim())
        .filter(name => name && name.toLowerCase() !== 'username');
      this.bulkText = names.join('\n');
      this.bulkResult = null;
    };
    reader.readAsText(file);
    input.value = '';
  }

  get bulkNames(): string[] {
    return Array.from(new Set(this.bulkText.split(/[\n,]/).map(s => s.trim()).filter(Boolean)));
  }

  onCheckList() {
    const names = this.bulkNames;
    if (names.length === 0 || names.length > MAX_BULK) {
      return;
    }
    this.checking = true;
    this.subscriptions.add(this.betLimitGroupDataService.resolveUsernames(this.data.provider.game_provider_id, names).subscribe(
      result => {
        this.bulkResult = result;
        result.found.filter(c => !this.isMember(c)).forEach(c => this.picked.set(c.id, c));
        this.checking = false;
      },
      () => this.checking = false
    ));
  }

  get bulkMoveCount(): number {
    return this.bulkResult ? this.bulkResult.found.filter(c => this.willMove(c)).length : 0;
  }

  get bulkMemberCount(): number {
    return this.bulkResult ? this.bulkResult.found.filter(c => this.isMember(c)).length : 0;
  }

  get maxBulk(): number {
    return MAX_BULK;
  }

  onConfirm() {
    this.dialogRef.close(Array.from(this.picked.values()));
  }

  onCloseDialog() {
    this.dialogRef.close([]);
  }
}
